import axios from 'axios';
import { omit, pickBy, uniq } from 'lodash';

import { RESET_ALL as RESET_ON_LOGOUT, danger, success } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { createReducer } from 'utils/redux';
import { API, destroy, get, post as postRequest, put } from 'utils/api';
import { t } from 'utils/language';
import { snakeCaseKeys } from 'utils/keys';
import { Querystring } from 'utils/url';
import { DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS, DELIVERABLE_ATTACHMENT_TYPE_HIGHRES, getModifiedDeliverable, isRegramContent } from 'utils/deliverables';
import { logError } from 'utils/log';

import type {
  Action,
  AdminAnalytic,
  Deliverable,
  DeliverableAttachment,
  DeliverablePost,
  DeliverableTag,
  DeliverableType,
  Dispatch,
  GetState,
  LocationQuery,
  PaginationLinks,
  PaginationMeta,
  ThunkAction
} from 'types';
import { Pagination } from 'types';

type Results = {
  data: Array<Deliverable>,
  meta: PaginationMeta,
  links: PaginationLinks
};

const { REPORTS } = API.END_POINTS;

const DELIVERABLE_UPDATED = 'reporting/metrics/DELIVERABLE_UPDATED';
const DELIVERABLES_LOADING = 'reporting/metrics/DELIVERABLES_LOADING';
const DELIVERABLES_LOADED = 'reporting/metrics/DELIVERABLES_LOADED';
const DELIVERABLES_ADDED = 'reporting/metrics/DELIVERABLES_ADDED';
const DELIVERABLES_RESET = 'reporting/metrics/DELIVERABLES_RESET';
const ANALYTICS_FETCHED = 'reporting/metrics/ANALYTICS_FETCHED';
const ANALYTIC_ADDED = 'reporting/metrics/ANALYTIC_ADDED';
const ANALYTIC_UPDATED = 'reporting/metrics/ANALYTIC_UPDATED';
const ANALYTIC_DELETED = 'reporting/metrics/ANALYTIC_DELETED';
const ANALYTIC_REORDER = 'reporting/metrics/ANALYTIC_REORDER';
const ATTACHMENT_ADDED = 'reporting/metrics/ATTACHMENT_ADDED';
const ATTACHMENT_REMOVED = 'reporting/metrics/ATTACHMENT_REMOVED';
const POST_ADDED = 'reporting/metrics/POST_ADDED';
const POST_REMOVED = 'reporting/metrics/POST_REMOVED';
const SNIPPET_MATCH = 'reporting/metrics/SNIPPET_MATCH';
const ACTIVE_DELIVERABLE_SET = 'reporting/metrics/ACTIVE_DELIVERABLE_SET';
const FB_DATA_UPDATED = 'reporting/metrics/FB_DATA_UPDATED';

type State = {
  deliverables: {
    items: Array<Deliverable>,
    isLoading: boolean
  },
  activeDeliverableId: number | null,
  pagination: Pagination
};

const initialState: State = {
  deliverables: {
    items: [],
    isLoading: false
  },
  activeDeliverableId: null,
  pagination: {}
};

const DEFAULT_SORTING = 'updated_date.desc';
let fetchCreatorsCancelToken;

const setLoadingStatus = (type: string, status: boolean): Action => ({
  type,

  payload: {
    status
  }
});

export const setActiveDeliverable = (deliverableId: number | null): Action => ({
  type: ACTIVE_DELIVERABLE_SET,

  payload: {
    deliverableId
  }
});

export const updateDeliverableFBDataEnabled = (
  deliverableId: number,
  dataEnabled: boolean
): Action => ({
  type: FB_DATA_UPDATED,
  payload: {
    deliverableId,
    dataEnabled
  }
});

const deliverableUpdated = (
  deliverableId: number,
  deliverableType: DeliverableType,
  deliverable: Deliverable
): Action => ({
  type: DELIVERABLE_UPDATED,

  payload: {
    deliverableId,
    deliverableType,
    deliverable
  }
});

const deliverablesLoaded = (results: Results): Action => ({
  type: DELIVERABLES_LOADED,

  payload: {
    results
  }
});

export const addDeliverableEntry = (entry: Deliverable): Action => ({
  type: DELIVERABLES_ADDED,

  payload: {
    entry
  }
});

const matchMetric = (deliverableId: number, attachmentId: number, analyticId: number): Action => ({
  type: SNIPPET_MATCH,

  payload: {
    deliverableId,
    attachmentId,
    analyticId
  }
});

const metricsAnalyticFetched = (deliverableId: number, analytics): Action => ({
  type: ANALYTICS_FETCHED,

  payload: {
    deliverableId,
    analytics
  }
});

const metricsAnalyticAdded = (deliverableId: number, analytic): Action => ({
  type: ANALYTIC_ADDED,

  payload: {
    deliverableId,
    analytic
  }
});

const metricsAnalyticUpdated = (deliverableId: number, analytic): Action => ({
  type: ANALYTIC_UPDATED,

  payload: {
    deliverableId,
    analytic
  }
});

const metricsAnalyticDeleted = (deliverableId: number, metricsId: number): Action => ({
  type: ANALYTIC_DELETED,

  payload: {
    deliverableId,
    metricsId
  }
});

const metricsAnalyticReordered = (deliverableId: number, from: number, to: number): Action => ({
  type: ANALYTIC_REORDER,

  payload: {
    deliverableId,
    from,
    to
  }
});

export const metricsAttachmentAdded = (deliverableId: number, attachment: DeliverableAttachment): Action => ({
  type: ATTACHMENT_ADDED,

  payload: {
    deliverableId,
    attachment
  }
});

export const metricsAttachmentRemoved = (deliverableId: number, attachmentId: number | string): Action => ({
  type: ATTACHMENT_REMOVED,

  payload: {
    deliverableId,
    attachmentId
  }
});

export const reportMetricsPostAdded = (deliverableId: number, post: DeliverablePost): Action => ({
  type: POST_ADDED,

  payload: {
    deliverableId,
    post
  }
});

export const reportMetricsPostRemoved = (deliverableId: number): Action => ({
  type: POST_ADDED,

  payload: {
    deliverableId
  }
});

export const resetDeliverables = (): Action => ({
  type: DELIVERABLES_RESET
});

const handleCancellationToken = (): void => {
  if (fetchCreatorsCancelToken) {
    fetchCreatorsCancelToken.cancel();
  }

  fetchCreatorsCancelToken = axios.CancelToken.source();
};

const getQueryString = (query: LocationQuery): string => {
  const params = snakeCaseKeys({
    page: query.page || 1,
    itemsPerPage: 50,
    status: query.status || '',
    type: query.type || 'instagram_post,instagram_story,instagram_reel,tiktok_video',
    sort: query.sort ? decodeURIComponent(query.sort) : DEFAULT_SORTING,
    brandIds: query.brandId || undefined,
    campaignIds: query.campaignId || undefined,
    creatorIds: query.influencerId || undefined,
    mismatchedAnalytics: query.mismatchedAnalytics === 'true' || undefined
  });

  const nonEmptyParams = pickBy(params);

  return Querystring.objectToString(nonEmptyParams);
};

export const getReportDeliverablesResponse = async (url: string): Promise<Results> => {
  const response = await get(url, {
    cancelToken: fetchCreatorsCancelToken.token
  });
  const results = response.data || [];
  results.data = getModifiedDeliverable(results.data || []);

  return results;
};

const genericFetchDeliverables = (url: string): ThunkAction => async (dispatch: Dispatch): Promise<void> => {
  handleCancellationToken();

  dispatch(setLoadingStatus(DELIVERABLES_LOADING, true));

  try {
    const results = await getReportDeliverablesResponse(url);

    dispatch(setLoadingStatus(DELIVERABLES_LOADING, false));
    dispatch(deliverablesLoaded(results));
    dispatch(setActiveDeliverable(null));
  } catch (error) {
    if (axios.isCancel(error)) {
      return;
    }

    dispatch(setLoadingStatus(DELIVERABLES_LOADING, false));

    throw error;
  }
};

export const fetchDeliverables = (campaignId: number, query?: LocationQuery | null): ThunkAction => {
  const queryString = getQueryString(query || {});
  const url = REPORTS.metrics.getDeliverables(campaignId, queryString);

  return genericFetchDeliverables(url);
};

export const fetchDeliverablesWithQueryOnly = (query?: LocationQuery | null): ThunkAction => {
  const queryString = getQueryString(query || {});
  const url = REPORTS.metrics.getDeliverablesQueryOnly(queryString);

  return genericFetchDeliverables(url);
};

export const deleteAdditionalDeliverable = (offerId: number, deliverableId: number) => (
  async (dispatch: Dispatch) => {
    const url = REPORTS.metrics.deleteAdditionalDeliverable(offerId, deliverableId);
    dispatch(showLoading(url));

    try {
      await destroy(url, { suppressGlobalNotification: true });
      dispatch(hideLoading(url));
      dispatch(success(t('admin_deliverables_metrics_page.delete_deliverable_success')));
    } catch (error) {
      dispatch(hideLoading(url));
      dispatch(danger(t('admin_deliverables_metrics_page.delete_deliverable_error')));
      throw error;
    }
  }
);

export const fetchReportMetricAnalytic = (deliverableId: number): ThunkAction => async (dispatch: Dispatch) => {
  const url = REPORTS.metrics.fetchReportMetricAnalytic(deliverableId);

  dispatch(showLoading(url));

  try {
    const response = await get(url);

    dispatch(hideLoading(url));

    dispatch(metricsAnalyticFetched(deliverableId, response.data));
  } catch (error) {
    dispatch(hideLoading(url));
    throw error;
  }
};

export const changeAdditionalDeliverable = (
  deliverableId: number,
  offerId: number,
  data: {
    tag?: DeliverableTag,
    deliverableType: DeliverableType
  }
) => (
  async (dispatch: Dispatch) => {
    const url = REPORTS.metrics.updateAdditionalDeliverable(offerId, deliverableId);
    const regram = isRegramContent(data.deliverableType);
    const deliverableType = data.deliverableType.replace('_regram', '');
    const modifiedData = { ...data, regram, deliverableType };

    dispatch(showLoading(url));

    try {
      const response = await put(url, { data: modifiedData });

      dispatch(hideLoading(url));
      dispatch(deliverableUpdated(deliverableId, data.deliverableType, response.data));

      // consider the fact that updated deliverable is always expanded, because how could you modify it otherwise
      dispatch(setActiveDeliverable(response.data.id));
    } catch (error) {
      dispatch(hideLoading(url));
      dispatch(danger(t('admin_deliverables_metrics_page.save_deliverable_error')));
      throw error;
    }
  }
);

export const addReportMetrics = (
  deliverableId: number,
  campaignId: number,
  data: {
    deliverableId: number,
    metricsType: string
  }
): ThunkAction => async (dispatch: Dispatch) => {
  const url = REPORTS.metrics.addMetrics(campaignId);

  dispatch(showLoading(url));

  try {
    const response = await postRequest(url, { data });

    dispatch(hideLoading(url));
    dispatch(metricsAnalyticAdded(deliverableId, response.data));
  } catch (error) {
    dispatch(hideLoading(url));
    dispatch(danger(t('admin_deliverables_metrics_page.save_deliverable_error')));
    throw error;
  }
};

export const updateReportMetrics = (
  deliverableId: number,
  campaignId: number,
  metricsId: number,
  data: Partial<AdminAnalytic>
): ThunkAction => async (dispatch) => {
  const url = REPORTS.metrics.updateMetrics(campaignId, metricsId);

  dispatch(showLoading(url));

  try {
    const response = await put(url, { data });

    dispatch(hideLoading(url));
    dispatch(metricsAnalyticUpdated(deliverableId, response.data));
  } catch (error) {
    dispatch(hideLoading(url));
    throw error;
  }
};

export const deleteReportStoryMetrics = (deliverableId: number, campaignId: number, metricsId: number): ThunkAction => async (dispatch: Dispatch) => {
  const url = REPORTS.metrics.deleteStoryMetrics(campaignId, metricsId);

  dispatch(showLoading(url));

  try {
    await destroy(url, { suppressGlobalNotification: true });

    dispatch(hideLoading(url));
    dispatch(metricsAnalyticDeleted(deliverableId, metricsId));
  } catch (error) {
    dispatch(hideLoading(url));
    throw error;
  }
};

export const reportMetricsSnippetMatch = (
  deliverableId: number,
  attachmentId: number,
  analyticId: number,
  sourceAnalyticId: number
): ThunkAction => async (dispatch: Dispatch, getState: GetState) => {
  try {
    const currentMetrics = getState().campaignReportingMetrics.deliverables.items;
    const deliverable = currentMetrics.find((entity) => entity.id === deliverableId);

    if (!deliverable || !deliverable.analytics) {
      return;
    }

    const attachment = deliverable.postAttachments?.find(entity => entity.id === attachmentId);
    const attachmentMetaType = attachment?.meta && attachment.meta.type;

    if (analyticId < 0) {
      const url = REPORTS.metrics.unmatchAnalyticAsset(deliverableId, sourceAnalyticId, attachmentId);

      await destroy(url);
    } else {
      let attachmentType;

      if (attachmentMetaType === DELIVERABLE_ATTACHMENT_TYPE_HIGHRES) {
        attachmentType = 'snippet';
      } else if (attachmentMetaType === DELIVERABLE_ATTACHMENT_TYPE_ANALYTICS) {
        attachmentType = 'analytics';
      }

      if (!attachmentType) {
        return;
      }

      const url = REPORTS.metrics.matchAssetToAnalytic(deliverableId, analyticId, attachmentType);
      const data = { id: attachmentId };

      await postRequest(url, { data });
    }

    dispatch(matchMetric(deliverableId, attachmentId, analyticId));
    dispatch(fetchReportMetricAnalytic(deliverableId));
  } catch (error) {
    const {
      code,
      status
    } = (error?.response.data ?? {});

    if (status === 409 && code === 'assets.relations.no_parent') {
      dispatch(danger(t('campaign_report_metrics.errors.no_parent_snippet')));
    } else if (status === 409 && code === 'metrics.snippets.already_matched') {
      dispatch(danger(t('campaign_report_metrics.errors.snippet_already_matched')));
    } else {
      logError(error);
    }
  }
};

export const reportMetricsReorder = (deliverableId: number, from: number, to: number): ThunkAction => async (dispatch: Dispatch, getState: GetState) => {
  const currentMetrics = getState().campaignReportingMetrics.deliverables.items;
  const deliverable = currentMetrics.find(entity => entity.id === deliverableId);

  if (!deliverable || !deliverable.analytics) {
    return;
  }

  const analyticToMove = deliverable.analytics[from] || {};
  const analyticToMoveAfter = deliverable.analytics[(from > to) ? to - 1 : to] || {};
  const url = REPORTS.metrics.reorderReportMetrics(deliverableId, analyticToMove.id);

  dispatch(metricsAnalyticReordered(deliverableId, from, to));

  try {
    await postRequest(url, {
      data: {
        afterMetricsId: to === 0 ? null : analyticToMoveAfter.id
      }
    });
  } catch (error) {
    // revert in case of error
    dispatch(metricsAnalyticReordered(deliverableId, to, from));

    throw error;
  }
};

const getSnippetMatchObject = (deliverables, deliverableId, attachmentId, analyticId) => {
  const getAnalytics = (analytics) => (
    (analytics || []).map(analytic => {
      const currentlyMatchedAttachments = analytic.matchedAttachments || [];

      if (analytic.id === analyticId) {
        return {
          ...analytic,
          matchedAttachments: uniq([attachmentId, ...currentlyMatchedAttachments])
        };
      }

      return {
        ...analytic,
        matchedAttachments: currentlyMatchedAttachments.filter(id => id !== attachmentId)
      };
    })
  );

  const updatedDeliverables = (deliverables.items || []).map(deliverable => {
    if (deliverable.id === deliverableId) {
      const analytics = getAnalytics(deliverable.analytics);

      return {
        ...deliverable,
        analytics
      };
    }

    return deliverable;
  });

  return ({
    ...deliverables,
    items: updatedDeliverables
  });
};

const getAnalyticsFetchedObject = (deliverables, deliverableId, analytics) => {
  const updatedDeliverables = (deliverables.items || []).map(deliverable => {
    if (deliverable.id === deliverableId) {
      return {
        ...deliverable,
        analytics
      };
    }

    return deliverable;
  });

  return ({
    ...deliverables,
    items: updatedDeliverables
  });
};

const getAnalyticAddedObject = (deliverables, deliverableId, analytic) => {
  const updatedDeliverables = (deliverables.items || []).map(deliverable => {
    if (deliverable.id === deliverableId) {
      return {
        ...deliverable,
        analytics: [...deliverable.analytics, analytic]
      };
    }

    return deliverable;
  });

  return ({
    ...deliverables,
    items: updatedDeliverables
  });
};

const getAnalyticUpdatedObject = (deliverables, deliverableId, analytic) => {
  const updatedDeliverables = (deliverables.items || []).map(deliverable => {
    if (deliverable.id === deliverableId) {
      return {
        ...deliverable,
        analytics: deliverable.analytics.reduce((res, item) => {
          res.push(item.id === analytic.id ? analytic : item);

          return res;
        }, [])
      };
    }

    return deliverable;
  });

  return ({
    ...deliverables,
    items: updatedDeliverables
  });
};

const getAnalyticDeletedObject = (deliverables, deliverableId, metricsId) => {
  const updatedDeliverables = (deliverables.items || []).map(deliverable => {
    if (deliverable.id === deliverableId) {
      return {
        ...deliverable,
        analytics: (deliverable.analytics || []).filter(analytic => analytic.id !== metricsId)
      };
    }

    return deliverable;
  });

  return ({
    ...deliverables,
    items: updatedDeliverables
  });
};

const getAnalyticReorderedObject = (deliverables, deliverableId, from, to) => {
  const updatedDeliverables = (deliverables.items || []).map(deliverable => {
    if (deliverable.id === deliverableId) {
      const originalAnalytics = deliverable.analytics || [];
      const analytics = [...originalAnalytics];

      // insert into position
      if (from > to) {
        analytics.splice(to, 0, originalAnalytics[from]);
        analytics.splice(from + 1, 1);
      } else {
        analytics.splice(to + 1, 0, originalAnalytics[from]);
        analytics.splice(from, 1);
      }

      return {
        ...deliverable,
        analytics
      };
    }

    return deliverable;
  });

  return ({
    ...deliverables,
    items: updatedDeliverables
  });
};

const getAttachmentAddedObject = (deliverables, deliverableId, attachment) => {
  const updatedDeliverables = (deliverables.items || []).map(deliverable => {
    if (deliverable.id === deliverableId) {
      return {
        ...deliverable,
        postAttachments: [
          ...deliverable.postAttachments,
          attachment
        ]
      };
    }

    return deliverable;
  });

  return ({
    ...deliverables,
    items: updatedDeliverables
  });
};

const getAttachmentRemovedObject = (deliverables, deliverableId, attachmentId) => {
  const updatedDeliverables = (deliverables.items || []).map(deliverable => {
    if (deliverable.id === deliverableId) {
      return {
        ...deliverable,
        postAttachments: (deliverable.postAttachments || []).filter(a => a.id !== attachmentId)
      };
    }

    return deliverable;
  });

  return ({
    ...deliverables,
    items: updatedDeliverables
  });
};

const getPostAddedObject = (deliverables, deliverableId, post) => {
  const updatedDeliverables = (deliverables.items || []).map(deliverable => {
    if (deliverable.id === deliverableId) {
      return {
        ...deliverable,
        post
      };
    }

    return deliverable;
  });

  return ({
    ...deliverables,
    items: updatedDeliverables
  });
};

const getPostRemovedObject = (deliverables, deliverableId) => {
  const updatedDeliverables = (deliverables.items || []).map(deliverable => {
    if (deliverable.id === deliverableId) {
      return omit(deliverable, 'post');
    }

    return deliverable;
  });

  return ({
    ...deliverables,
    items: updatedDeliverables
  });
};

const getDeliverableUpdatedObject = (
  deliverables,
  deliverableId,
  deliverableType,
  updatedDeliverable
) => {
  const updatedDeliverables = (deliverables.items || []).map(deliverable => {
    if (deliverable.id === deliverableId) {
      return {
        ...deliverable,
        ...updatedDeliverable,
        tags: deliverable.tags,
        deliverableType,
        previousId: deliverable.id
      };
    }

    return deliverable;
  });

  return ({
    ...deliverables,
    items: updatedDeliverables
  });
};

export default createReducer<State, any>('campaignReportingMetrics', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [DELIVERABLE_UPDATED]: (state, { deliverableId, deliverableType, deliverable }) => ({
    ...state,
    deliverables: getDeliverableUpdatedObject(state.deliverables, deliverableId, deliverableType, deliverable)
  }),
  [DELIVERABLES_LOADING]: (state, { status }) => ({
    ...state,
    deliverables: {
      ...state.deliverables,
      isLoading: status
    }
  }),
  [DELIVERABLES_LOADED]: (state, { results }) => ({
    ...state,
    deliverables: {
      items: results.data,
      isLoading: false
    },
    pagination: {
      ...results.meta,
      links: results.links
    }
  }),
  [DELIVERABLES_ADDED]: (state, { entry }) => ({
    ...state,
    deliverables: {
      items: [entry, ...state.deliverables.items],
      isLoading: false
    }
  }),
  [DELIVERABLES_RESET]: (state) => ({
    ...state,
    deliverables: initialState.deliverables
  }),
  [SNIPPET_MATCH]: (state, { deliverableId, attachmentId, analyticId }) => ({
    ...state,
    deliverables: getSnippetMatchObject(state.deliverables, deliverableId, attachmentId, analyticId)
  }),
  [ANALYTICS_FETCHED]: (state, { deliverableId, analytics }) => ({
    ...state,
    deliverables: getAnalyticsFetchedObject(state.deliverables, deliverableId, analytics)
  }),
  [ANALYTIC_ADDED]: (state, { deliverableId, analytic }) => ({
    ...state,
    deliverables: getAnalyticAddedObject(state.deliverables, deliverableId, analytic)
  }),
  [ANALYTIC_UPDATED]: (state, { deliverableId, analytic }) => ({
    ...state,
    deliverables: getAnalyticUpdatedObject(state.deliverables, deliverableId, analytic)
  }),
  [ANALYTIC_DELETED]: (state, { deliverableId, metricsId }) => ({
    ...state,
    deliverables: getAnalyticDeletedObject(state.deliverables, deliverableId, metricsId)
  }),
  [ANALYTIC_REORDER]: (state, { deliverableId, from, to }) => ({
    ...state,
    deliverables: getAnalyticReorderedObject(state.deliverables, deliverableId, from, to)
  }),
  [ATTACHMENT_ADDED]: (state, { deliverableId, attachment }) => ({
    ...state,
    deliverables: getAttachmentAddedObject(state.deliverables, deliverableId, attachment)
  }),
  [ATTACHMENT_REMOVED]: (state, { deliverableId, attachmentId }) => ({
    ...state,
    deliverables: getAttachmentRemovedObject(state.deliverables, deliverableId, attachmentId)
  }),
  [POST_ADDED]: (state, { deliverableId, post }) => ({
    ...state,
    deliverables: getPostAddedObject(state.deliverables, deliverableId, post)
  }),
  [POST_REMOVED]: (state, { deliverableId }) => ({
    ...state,
    deliverables: getPostRemovedObject(state.deliverables, deliverableId)
  }),
  [ACTIVE_DELIVERABLE_SET]: (state, { deliverableId }) => ({
    ...state,
    activeDeliverableId: deliverableId
  }),
  [FB_DATA_UPDATED]: (state, { deliverableId, dataEnabled }) => {
    const updatedDeliverables = state.deliverables.items
      .map(item => {
        if (item.id === deliverableId) {
          // per COLLAB-16027, delete analytics, snippets, urls, etc. when disabling FB data
          return {
            ...item,
            isFacebookDataEnabled: dataEnabled,
            analytics: [],
            post: {} as DeliverablePost,
            postAttachments: []
          };
        }

        return item;
      });

    return {
      ...state,
      deliverables: {
        ...state.deliverables,
        items: updatedDeliverables
      }
    };
  }
});
